import React, { useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { destroySocket, getSocket } from "./getSocket";
import { SocketContext, SocketConnectionStatus } from "./SocketContext";
import { useAppSelector } from "@store/hooks";

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const socketRef = useRef<Socket | null>(null);
  const token = useAppSelector((state) => state.auth.token);

  const [connectionStatus, setConnectionStatus] =
    useState<SocketConnectionStatus>("idle");
  const [serverReady, setServerReady] = useState(false);

  useEffect(() => {
    // GUARD: No token = No connection
    if (!token) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        destroySocket();
        socketRef.current = null;
      }
      setConnectionStatus("idle");
      return;
    }

    // 1. Get/Create Instance
    const socket = getSocket();
    socketRef.current = socket;
    // 2. Attach Lifecycle Listeners (The "Status" logic)
    socket.on("connect", () => setConnectionStatus("connected"));
    socket.on("disconnect", () => {
      setConnectionStatus("disconnected");
      setServerReady(false);
    });
    socket.on("server:ready", () => setServerReady(true));

    // 3. Connect with Token
    socket.auth = { token };

    if (!socket.connected) {
      socket.connect();
    }
    // 4. CLEANUP (The "Inevitable" part you asked about)
    // This ensures that when the token changes, we don't leave
    // listeners behind on the old socket instance.
    return () => {
      // socket.removeAllListeners();
      // socket.disconnect();

      socket.off("connect");
      socket.off("disconnect");
      socket.off("server:ready");
    };
  }, [token]); // Only re-runs if the token itself changes

  return (
    <SocketContext.Provider
      value={{ socket: socketRef.current!, connectionStatus, serverReady }}
    >
      {children}
    </SocketContext.Provider>
  );
};
